import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Card, Tag, Typography, Button, Descriptions } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { getPayouts } from '../../services/payout'
import { formatTimestamp } from '../../utils/format'
import type { Payout, Pagination } from '../../types'

const { Title } = Typography

const statusLabels: Record<string, string> = {
  PENDING: '待審核',
  APPROVED: '已審批',
  PROCESSING: '處理中',
  COMPLETED: '已完成',
  REJECTED: '已拒絕',
  FAILED: '失敗'
}

const statusColors: Record<string, string> = {
  PENDING: 'gold',
  APPROVED: 'blue',
  PROCESSING: 'cyan',
  COMPLETED: 'green',
  REJECTED: 'red',
  FAILED: 'red'
}

const formatProcessingDate = (timestamp: number): string => new Date(timestamp).toLocaleDateString('zh-CN', {
  timeZone: 'Asia/Shanghai',
})

export default function PayoutList() {
  const [payouts, setPayouts] = useState<Payout[]>([])
  const [pagination, setPagination] = useState<Pagination>({ page: 1, size: 20, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()

  const fetchPayouts = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getPayouts({ page, size })
      .then((res) => {
        setPayouts(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchPayouts()
  }, [])

  const formatAmount = (amount: string | number) => Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const handleCreatePayout = () => {
    navigate('/dashboard/payouts/new')
  }

  const columns = [
    { title: '結款 ID', dataIndex: 'id', key: 'id' },
    {
      title: '金額',
      key: 'amount',
      render: (_: unknown, record: Payout) => `$${formatAmount(record.amount)} ${record.currency}`
    },
    {
      title: '手續費',
      key: 'fee',
      render: (_: unknown, record: Payout) => `$${formatAmount(record.fee)}`
    },
    {
      title: '實際到賬',
      key: 'netAmount',
      render: (_: unknown, record: Payout) => `$${formatAmount(record.netAmount)}`
    },
    {
      title: '方式',
      key: 'method',
      render: (_: unknown, record: Payout) => (
        <div>
          <div>{record.method}</div>
          {record.usdtServiceType && (
            <Tag color={record.usdtServiceType === 'URGENT' ? 'orange' : 'blue'}>
              {record.usdtServiceType === 'URGENT' ? '緊急提現' : '普通提現'}
            </Tag>
          )}
        </div>
      )
    },
    {
      title: '預計處理／到賬',
      dataIndex: 'expectedProcessingAt',
      key: 'expectedProcessingAt',
      render: (ts: number | undefined, record: Payout) => ts
        ? record.usdtServiceType === 'URGENT'
          ? new Date(ts).toLocaleString('zh-CN', { timeZone: 'Asia/Hong_Kong', hour12: false })
          : formatProcessingDate(ts)
        : '-'
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      )
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (ts: number) => formatTimestamp(ts)
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>結款記錄</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreatePayout}>
          發起結款
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={payouts}
          rowKey="id"
          loading={loading}
          expandable={{
            rowExpandable: (record) => record.usdtServiceType != null && record.payoutFeeFixed != null,
            expandedRowRender: (record) => (
              <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                <Descriptions.Item label="提交時費率">
                  ${formatAmount(record.payoutFeeFixed || 0)} + {(Number(record.payoutFeeRate || 0) * 100).toFixed(2)}%
                </Descriptions.Item>
                <Descriptions.Item label="比例手續費">
                  ${formatAmount(record.payoutPercentageFee || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="網絡費">
                  ${formatAmount(record.payoutNetworkFee || 0)}
                </Descriptions.Item>
                <Descriptions.Item label="手續費合計">
                  ${formatAmount(record.fee)}
                </Descriptions.Item>
              </Descriptions>
            ),
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            onChange: (page, size) => {
              if (size !== pageSize) {
                setPageSize(size)
                fetchPayouts(1, size)
              } else {
                fetchPayouts(page, size)
              }
            },
          }}
        />
      </Card>
    </div>
  )
}
